/**
 * Host-side React hook that runs ELK layered layout.
 *
 * elkjs ships its own worker shim — `elk.bundled.js` returns a Promise
 * even though the in-process compute is synchronous. We previously
 * wrapped this in a Vite `?worker`, but that double-wrap breaks elkjs's
 * internal `elk-worker.min.js` UMD: inside a Web Worker its
 * `typeof document === "undefined"` branch registers `self.onmessage`
 * instead of exporting the `Worker` constructor, leaving the bundled
 * entry with an undefined factory and producing `L8 is not a
 * constructor` after minification.
 */
import type { CircuitIR } from "@models/circuit-ir";
import ELK from "elkjs/lib/elk.bundled.js";
import { useEffect, useMemo, useState } from "react";

import { type IrToElkOptions, irToElkGraph } from "./irToElkGraph";

export interface ElkLayoutedGraph {
  children?: ElkLayoutedNode[];
  edges?: ElkLayoutedEdge[];
}

export interface ElkLayoutedNode {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  labels?: { text?: string; x?: number; y?: number }[];
  ports?: ElkLayoutedPort[];
}

export interface ElkLayoutedPort {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface ElkPoint {
  x: number;
  y: number;
}

export interface ElkEdgeSection {
  startPoint: ElkPoint;
  endPoint: ElkPoint;
  bendPoints?: ElkPoint[];
}

export interface ElkLayoutedEdge {
  id: string;
  sources: string[];
  targets: string[];
  sections?: ElkEdgeSection[];
}

export interface UseElkLayoutResult {
  graph: ElkLayoutedGraph | null;
  isLoading: boolean;
  error: string | null;
}

const ElkCtor = (ELK as unknown as { default?: typeof ELK }).default ?? ELK;
const elk = new ElkCtor();

export function useElkLayout(
  ir: CircuitIR | null | undefined,
  options?: IrToElkOptions,
): UseElkLayoutResult {
  const [state, setState] = useState<UseElkLayoutResult>({
    graph: null,
    isLoading: false,
    error: null,
  });

  const optionsKey = useMemo(() => JSON.stringify(options ?? {}), [options]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: optionsKey covers the JSON-stable subset.
  useEffect(() => {
    if (!ir) {
      setState({ graph: null, isLoading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, error: null }));

    (async () => {
      try {
        const input = irToElkGraph(ir, options);
        const layouted = await elk.layout(input as never);
        if (cancelled) return;
        setState({
          graph: layouted as ElkLayoutedGraph,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          graph: null,
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ir, optionsKey]);

  return state;
}
